import { useEffect, useRef, useMemo, useState } from 'react';

import { css } from '@compiled/react';
import { FormattedMessage } from 'react-intl';
import { AGGClient } from 'src/clients/agg-client/agg-client';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { Tracking } from '@/analytics/atl-analytics/tracking';
import { useAnalyticsClient } from '@/analytics/wac-analytics/analytics-client';
import { useIsDesktop } from '@/components/user-context/helpers/cohorts';
import { Button } from '@/libraries/design/components/button/src/button';
import { LOCALES } from '@/libraries/web-framework/src/localization/localization.constants';
import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href';
import { ProductKeyTypes } from '@/libraries/wpl/foundations/src/data-event-props';
import { dynamicConfigManifest } from '@/utils/dynamic-configs/dynamic-config-manifest';
import { getDynamicConfig } from '@/utils/dynamic-configs/get-dynamic-config/get-dynamic-config';
import { experiments } from '@/utils/experiment/manifest';
import { type Experiment, useExperiment } from '@/utils/experiment/use-experiment';
import { useExperimentExposure } from '@/utils/experiment/use-experiment-exposure';
import { useLocation } from '@/utils/location-provider';
import { useLoggedInUser } from '@/utils/logged-in-user-provider';

import {
	getIsConfluenceTourSmbSalesLead,
	getIsEnterpriseSalesLead,
	getIsSMBSalesLead,
} from './contact-sales-cta-helpers';

const fullWidthStyles = css({
	maxWidth: 'none',
	width: '100%',
});

// The desktop nav row does not centre its items, so without this the CTA sits
// above the avatar and app switcher.
const navAlignStyles = css({
	alignSelf: 'center',
});

const SALES_LEADS_ROUTES = ['/', '/software/confluence', '/software/jira'];

/**
 * The WAC homepage. `useLocation` can report the homepage as either '/' or an
 * empty string, which the surrounding nav code already defends against.
 */
const HOMEPAGE_ROUTES = ['/', ''];

/**
 * The Confluence product tour, exact match. Deliberately not locale-normalised:
 * the experiment is powered against the exact tour path, so localised tours
 * were never counted and must not be enrolled.
 */
const CONFLUENCE_TOUR_ROUTES = ['/software/confluence', '/software/confluence/'];

/**
 * Which eligibility path put the CTA on screen. Exactly one applies per render:
 * where two are eligible, `useAnyContactSalesCtaEligibility` picks one.
 */
type SalesLeadSegment = 'smb' | 'enterprise' | 'smbConfluenceTour';

/**
 * Every path renders the same button with the same label and container — so
 * without this the paths' events are indistinguishable in the warehouse. That
 * matters on '/', which is the only route the Enterprise experiment runs on and
 * is also an SMB route, and on the Confluence tour, where the shipped SMB CTA
 * still renders for the users it already serves. Those users are in both arms
 * of the tour experiment and keep the shipped path's tag in both, so
 * `contactSalesCtaSmbConfluenceTour` marks only the CTAs the experiment adds.
 *
 * The Statsig readouts do not depend on this, because they are keyed on each
 * experiment's own exposure, but any direct `event_ui` query does — including
 * the launch monitoring dashboards.
 *
 * `actionSubjectId` is deliberately NOT changed on the view event: that one
 * already ships as `contactSalesCta` and is a top-level column existing
 * dashboards filter on. The view event carries `leadSegment` instead.
 */
const CTA_CLICK_ACTION_SUBJECT_ID: Record<SalesLeadSegment, string> = {
	smb: 'contactSalesCtaSmb',
	enterprise: 'contactSalesCtaEnterprise',
	smbConfluenceTour: 'contactSalesCtaSmbConfluenceTour',
};

const createSalesInboundAggClient = () =>
	new AGGClient({
		gatewayUrl:
			process.env['NODE_ENV'] === 'production'
				? 'https://www.atlassian.com/gateway/api/graphql'
				: 'https://wac.stg.internal.atlassian.com/gateway/api/graphql',
	});

const toNonLocalizedRoute = (pathname: string): string => {
	const locale = LOCALES.find(
		(candidate) => pathname === `/${candidate}` || pathname.startsWith(`/${candidate}/`),
	);
	const withoutLocale = locale ? pathname.slice(locale.length + 1) : pathname;
	return withoutLocale.replace(/\/+$/, '') || '/';
};

export const isSalesLeadsCtaRoute = (routes: string[], pathname: string): boolean =>
	routes.some((route) => toNonLocalizedRoute(route) === toNonLocalizedRoute(pathname));

export const useContactSalesCtaEligibility = (): {
	isEligible: boolean;
	isLoading: boolean;
} => {
	const { accountId } = useLoggedInUser();
	const { pathname } = useLocation();
	// the CTA is only rendered on the page paths listed in this dynamic config
	// https://switcheroo.atlassian.com/ui/dynamic-configs/key/wac-sales-leads-cta-route-list
	const routes = getDynamicConfig({
		config: dynamicConfigManifest.routesAllowingSalesLeadsCta,
		defaultValue: SALES_LEADS_ROUTES,
	});
	const isRouteAllowed = isSalesLeadsCtaRoute(routes, pathname);

	const [isSalesLead, setIsSalesLead] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	useEffect(() => {
		if (!isRouteAllowed || !accountId) return;
		setIsLoading(true);

		getIsSMBSalesLead({
			accountId,
			aggClient: createSalesInboundAggClient(),
		})
			.then((response) => {
				setIsSalesLead(!!response);
			})
			.catch(() => {
				console.error('Error fetching GUPS user profile for SMB Sales lead');
			})
			.finally(() => {
				setIsLoading(false);
			});
	}, [accountId, isRouteAllowed]);

	if (!accountId || !isRouteAllowed) {
		return { isEligible: false, isLoading: false };
	}

	return {
		isEligible: isSalesLead,
		isLoading,
	};
};

/**
 * Enterprise/Strategic eligibility for the global-nav "Contact us" CTA.
 * TWCG-2490 · Switcheroo FD-232121.
 *
 * Exposure timing is load-bearing and is the highest-risk detail in this
 * experiment:
 *
 *  1. BOTH arms run the GUPS account-segment lookup. The call in control is not
 *     waste — it *is* the eligibility gate that defines the comparable
 *     population. Gating the lookup on the cohort instead would leave control's
 *     Enterprise population unidentifiable, and the experiment unanalysable.
 *  2. The experiment is registered with `fireExperimentExposure: false`, so
 *     merely evaluating it enrols nobody.
 *  3. Exposure fires manually, and only once the lookup resolves to an eligible
 *     segment. Enrolling on homepage arrival instead would dilute enrolment
 *     from ~6,483 to ~71,358 users per 4 weeks (~11x) and collapse the MDE.
 *
 * Desktop only, per the P2c scope decision. The viewport guard lives here
 * rather than at the mount site because `mobile-nav-links-container.tsx`
 * renders the same CTA, so a mount-site-only guard would be easy to miss.
 */
export const useEnterpriseContactSalesCtaEligibility = (): {
	isEligible: boolean;
	isLoading: boolean;
} => {
	const { accountId } = useLoggedInUser();
	const { pathname } = useLocation();
	const isDesktop = useIsDesktop();

	// Exact homepage match, deliberately not locale-normalised: the audience
	// this experiment is powered against was measured on page_path = '/', so
	// localised homepages were never counted and must not be enrolled.
	const isHomepage = HOMEPAGE_ROUTES.includes(pathname);
	const shouldEvaluateSegment = isHomepage && !!isDesktop && !!accountId;

	const [isEnterpriseSalesLead, setIsEnterpriseSalesLead] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const experimentResult = useExperiment(experiments.twcg2490EnterpriseContactSalesCta);
	const [, fireExposure] = useExperimentExposure(experiments.twcg2490EnterpriseContactSalesCta);

	useEffect(() => {
		// `shouldEvaluateSegment` already includes `!!accountId`, and TypeScript
		// narrows `accountId` through it (control flow analysis of aliased
		// conditions), so no separate accountId guard is needed here.
		if (!shouldEvaluateSegment) {
			return;
		}

		setIsLoading(true);

		getIsEnterpriseSalesLead({
			accountId,
			aggClient: createSalesInboundAggClient(),
		})
			.then((response) => {
				setIsEnterpriseSalesLead(!!response);
			})
			.catch(() => {
				console.error('Error fetching GUPS account profile for Enterprise Sales lead');
			})
			.finally(() => {
				setIsLoading(false);
			});
	}, [accountId, shouldEvaluateSegment]);

	// Enrol only now that the user is known to be in the target segment. A GUPS
	// error, a timeout, or an absent/unrecognised segment must NOT enrol —
	// doing so inflates both denominators and biases the result toward null.
	//
	// The ref keeps this to exactly one exposure per mount. `fireExposure` is a
	// useCallback over the evaluated enrollment, so its identity changes on
	// every render; without the guard this effect would re-run and
	// `useExperimentExposure` would log an "already fired" warning each time.
	const hasFiredExposureRef = useRef(false);

	useEffect(() => {
		if (isEnterpriseSalesLead && !hasFiredExposureRef.current) {
			hasFiredExposureRef.current = true;
			fireExposure();
		}
	}, [isEnterpriseSalesLead, fireExposure]);

	if (!shouldEvaluateSegment) {
		return { isEligible: false, isLoading: false };
	}

	const isInTreatment = 'value' in experimentResult && experimentResult.value === true;

	return {
		isEligible: isEnterpriseSalesLead && isInTreatment,
		isLoading,
	};
};

type SegmentTargetedExperimentOptions = {
	experiment: Experiment<boolean>;
	/** Where the experiment runs (route, device), decided by the caller. */
	isTargetSurface: boolean;
	/**
	 * Must be referentially stable, such as a module-level helper. It is an
	 * effect dependency, so an inline arrow would restart the lookup on every
	 * render.
	 */
	lookupEligibility: (props: { accountId: string; aggClient?: AGGClient }) => Promise<boolean>;
	lookupErrorMessage: string;
};

/**
 * What a segment-targeted "Contact us" experiment needs once its surface is
 * known. Written for the Confluence product tour and shaped so the Jira tour
 * can reuse it; the Enterprise hook above predates it and is left unchanged
 * while its experiment runs.
 *
 * Exposure timing follows the Enterprise hook, for the same reasons:
 *  1. BOTH arms run the eligibility lookup. In control it is the gate that
 *     makes the control population comparable.
 *  2. The experiment is registered with `fireExperimentExposure: false`, so
 *     evaluating it enrols nobody.
 *  3. Exposure fires manually, once, and only after the lookup resolves
 *     eligible. A lookup error, a timeout or an ineligible profile never enrols.
 *
 * A failed lookup leaves the user in neither arm, so an outage would shrink
 * the experiment without anything looking wrong. Each mount reports its first
 * failure as an operational event, which makes that loss measurable.
 */
const useSegmentTargetedExperimentEligibility = ({
	experiment,
	isTargetSurface,
	lookupEligibility,
	lookupErrorMessage,
}: SegmentTargetedExperimentOptions): {
	isEligible: boolean;
	isLoading: boolean;
} => {
	const { accountId } = useLoggedInUser();
	const shouldEvaluateSegment = isTargetSurface && !!accountId;
	const analyticsClient = useAnalyticsClient();

	const [isTargetSegment, setIsTargetSegment] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [hasLookupFailed, setHasLookupFailed] = useState<boolean>(false);

	const experimentResult = useExperiment(experiment);
	const [, fireExposure] = useExperimentExposure(experiment);

	useEffect(() => {
		if (!shouldEvaluateSegment) {
			return;
		}

		// A lookup that settles after the account or surface has changed is
		// stale: it must neither enrol, nor report a failure, nor end a newer
		// lookup's loading state.
		let isCurrent = true;

		setIsTargetSegment(false);
		setHasLookupFailed(false);
		setIsLoading(true);

		lookupEligibility({
			accountId,
			aggClient: createSalesInboundAggClient(),
		})
			.then((response) => {
				if (isCurrent) {
					setIsTargetSegment(!!response);
				}
			})
			.catch(() => {
				if (isCurrent) {
					console.error(lookupErrorMessage);
					setHasLookupFailed(true);
				}
			})
			.finally(() => {
				if (isCurrent) {
					setIsLoading(false);
				}
			});

		return () => {
			isCurrent = false;
		};
	}, [accountId, shouldEvaluateSegment, lookupEligibility, lookupErrorMessage]);

	// Exactly one exposure per mount; see the Enterprise hook for why the ref
	// is needed.
	const hasFiredExposureRef = useRef(false);

	useEffect(() => {
		if (shouldEvaluateSegment && isTargetSegment && !hasFiredExposureRef.current) {
			hasFiredExposureRef.current = true;
			fireExposure();
		}
	}, [shouldEvaluateSegment, isTargetSegment, fireExposure]);

	// Reported from an effect rather than the catch handler, because the
	// analytics client can still be null when the lookup fails. The error text
	// is left out on purpose: it is free text from the GraphQL response.
	const hasReportedFailureRef = useRef(false);

	useEffect(() => {
		if (hasLookupFailed && analyticsClient && !hasReportedFailureRef.current) {
			hasReportedFailureRef.current = true;
			analyticsClient.sendOperationalEvent({
				action: 'failed',
				actionSubject: 'contactSalesCtaEligibilityLookup',
				source: 'globalNav',
				attributes: {
					experimentName: experiment.experimentName,
				},
			});
		}
	}, [hasLookupFailed, analyticsClient, experiment]);

	if (!shouldEvaluateSegment) {
		return { isEligible: false, isLoading: false };
	}

	const isInTreatment = 'value' in experimentResult && experimentResult.value === true;

	return {
		isEligible: isTargetSegment && isInTreatment,
		isLoading,
	};
};

/**
 * Confluence product tour experiment. TWCG-2633 · Switcheroo FD-234561.
 *
 * Enrols only on the exact tour, on desktop, for a logged-in SMB or SMB Plus
 * account, including the users the shipped SMB CTA already serves: they keep
 * seeing it in both arms. Control keeps the existing nav; treatment renders the
 * CTA in the nav's primary-CTA slot, which on the tour takes the place of the
 * product nav's "Get it free" (see NavCta).
 *
 * Desktop only, like the Enterprise experiment: on mobile the CTA sits inside
 * the collapsed menu, so enrolling at lookup time would count users who never
 * see it.
 */
export const useConfluenceTourContactSalesCtaEligibility = (): {
	isEligible: boolean;
	isLoading: boolean;
} => {
	const { pathname } = useLocation();
	const isDesktop = useIsDesktop();

	return useSegmentTargetedExperimentEligibility({
		experiment: experiments.twcg2633ConfluenceTourSmbContactSalesCta,
		isTargetSurface: CONFLUENCE_TOUR_ROUTES.includes(pathname) && !!isDesktop,
		lookupEligibility: getIsConfluenceTourSmbSalesLead,
		lookupErrorMessage: 'Error fetching GUPS profile for the Confluence tour SMB experiment',
	});
};

/**
 * Combined eligibility across the shipped SMB path and the two experiments.
 *  - Enterprise never overlaps the other two. `smbUserVisitedPricingPages` is
 *    SMB-only, confirmed with the GUPS team on 2026-09-18, so an
 *    Enterprise/Strategic account can never satisfy the SMB path, and the
 *    control baseline for the Enterprise experiment is genuinely zero. The
 *    Confluence tour runs on a route the Enterprise experiment does not.
 *  - The Confluence tour experiment does overlap the shipped SMB path: it
 *    enrols the users that path serves, in both arms.
 */
export const useAnyContactSalesCtaEligibility = (): {
	isEligible: boolean;
	isLoading: boolean;
	leadSegment: SalesLeadSegment | undefined;
} => {
	const smb = useContactSalesCtaEligibility();
	const enterprise = useEnterpriseContactSalesCtaEligibility();
	const confluenceTour = useConfluenceTourContactSalesCtaEligibility();

	// The shipped SMB path is labelled before the Confluence tour on purpose. A
	// user it serves sees the same CTA in both of the tour's arms, and labelling
	// it the same way in both keeps `smbConfluenceTour` to the CTAs the
	// experiment adds. Enterprise comes first defensively: if GUPS ever answered
	// two lookups inconsistently, its experiment's events stay its own.
	const leadSegment: SalesLeadSegment | undefined = enterprise.isEligible
		? 'enterprise'
		: smb.isEligible
			? 'smb'
			: confluenceTour.isEligible
				? 'smbConfluenceTour'
				: undefined;

	return {
		isEligible: smb.isEligible || enterprise.isEligible || confluenceTour.isEligible,
		isLoading: smb.isLoading || enterprise.isLoading || confluenceTour.isLoading,
		leadSegment,
	};
};

export const ContactSalesCta = ({ isFullWidth }: { isFullWidth?: boolean }) => {
	const { isEligible, isLoading, leadSegment } = useAnyContactSalesCtaEligibility();
	const productKey = useMemo<ProductKeyTypes>(() => {
		const pathname = globalThis.location?.pathname ?? '';
		if (pathname.includes('/software/jira')) return 'jira';
		if (pathname.includes('/software/confluence')) return 'confluence';
		if (pathname.includes('/enterprise/contact')) return 'enterpriseContactSales';
		if (pathname === '/') return 'home';
		return undefined;
	}, []);

	// `leadSegment` is defined exactly when at least one path is eligible, so
	// testing it also narrows the type for the renderer below.
	if (isLoading || !isEligible || !leadSegment) {
		return;
	}

	return (
		<CtaButtonRenderer
			isFullWidth={isFullWidth}
			productKey={productKey}
			leadSegment={leadSegment}
		/>
	);
};

const CtaButtonRenderer = ({
	isFullWidth,
	productKey,
	leadSegment,
}: {
	isFullWidth?: boolean;
	productKey: ProductKeyTypes;
	leadSegment: SalesLeadSegment;
}) => {
	const analyticsClient = useAnalyticsClient();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const localizedHref = useLocalizedHref('/enterprise/contact?formType=product-features');

	const hasFiredRef = useRef(false);

	useEffect(() => {
		if (analyticsClient && !hasFiredRef.current) {
			hasFiredRef.current = true;
			createAnalyticsEvent({
				eventType: 'ui',
				action: 'viewed',
				actionSubject: 'button',
				actionSubjectId: 'contactSalesCta',
				source: 'globalNav',
				attributes: {
					isMobile: !!isFullWidth,
					productKey: productKey,
					leadSegment,
				},
			}).fire();
		}
	}, [analyticsClient, createAnalyticsEvent, isFullWidth]);

	useEffect(() => {
		Tracking.autoTrackLinks(true);
	}, []);

	return (
		<Button
			variant="primary_alt"
			size={isFullWidth ? undefined : 'small'}
			css={[isFullWidth && fullWidthStyles, !isFullWidth && navAlignStyles]}
			href={localizedHref}
			dataEventProps={{
				'data-product-key': productKey,
				'data-event': 'clicked',
				'data-event-component': 'linkButton',
				'data-event-container': 'navbar',
				'data-label-english': 'Contact us',
				'data-action-subject-id': CTA_CLICK_ACTION_SUBJECT_ID[leadSegment],
			}}
		>
			<FormattedMessage id="global-nav.contact-sales-cta.label" defaultMessage="Contact us" />
		</Button>
	);
};
